import {useCallback, useEffect, useState} from "react";
import axios from "axios";
import {Box, Button, HStack, Icon, Select, Text, Spinner} from "@chakra-ui/react";
import {FiCheckCircle} from "react-icons/fi";
import {url} from "../../../crudRequests";
import EmbeddedCardSetup from "./EmbeddedCardSetup";

// Orchestrates the embedded "Payment Method" form field for BOTH the
// authenticated portal and the public email link. It asks the backend for a
// SetupIntent (authorized by `formAuth` — either the form's encrypted `params`
// or the patient's jwt+fsid), optionally prompts for a location when Stripe
// keys are per-location and ambiguous, mounts Stripe Elements inline, and on
// success records only {saved,last4,brand} back to the form via `onSaved`.

function buildConfig(formAuth) {
  const headers = {"Content-Type": "application/json", "X-Tenant": "patient"};
  if (formAuth?.jwt) headers.Authorization = "Bearer " + formAuth.jwt;
  return {headers};
}

function buildBody(formAuth, extra = {}) {
  const body = {...extra};
  if (formAuth?.params) body.params = formAuth.params;
  if (formAuth?.fsid) body.fsid = formAuth.fsid;
  return body;
}

export default function FormStripeCard({label, savedCard, onSaved, formAuth}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [setup, setSetup] = useState(null); // {clientSecret, stripePublicKey, stripeAccount, lid}
  const [locationOptions, setLocationOptions] = useState(null); // [{lid,name}] when ambiguous
  const [chosenLid, setChosenLid] = useState("");

  const isSaved = savedCard && typeof savedCard === "object" && savedCard.saved;

  const requestSetupIntent = useCallback(
    async (lid) => {
      setLoading(true);
      setError("");
      try {
        const res = await axios.post(
          `${url}/temp-form/setup-intent`,
          buildBody(formAuth, lid ? {lid} : {}),
          buildConfig(formAuth)
        );
        if (res.data?.needsLocation) {
          setLocationOptions(res.data.locations || []);
          setSetup(null);
        } else {
          setLocationOptions(null);
          setSetup(res.data);
        }
      } catch (e) {
        setError(
          e?.response?.data?.error ||
            "Could not load the payment form. Please contact the clinic."
        );
      } finally {
        setLoading(false);
      }
    },
    [formAuth]
  );

  useEffect(() => {
    if (!isSaved) requestSetupIntent(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConfirm = useCallback(
    async (paymentMethodId) => {
      const res = await axios.post(
        `${url}/temp-form/confirm-card`,
        buildBody(formAuth, {paymentMethodId, lid: setup?.lid || undefined}),
        buildConfig(formAuth)
      );
      onSaved({
        saved: true,
        last4: res.data?.last4 || "",
        brand: res.data?.brand || "",
      });
    },
    [formAuth, setup, onSaved]
  );

  if (isSaved) {
    return (
      <Box mt={2}>
        {label}
        <HStack
          p={3}
          mt={1}
          border="1px solid"
          borderColor="green.200"
          borderRadius="md"
          bg="green.50"
          spacing={2}
        >
          <Icon as={FiCheckCircle} color="green.500" />
          <Text fontSize="sm" color="gray.700">
            Payment method added
            {savedCard.last4 ? ` ····${savedCard.last4}` : ""}
            {savedCard.brand ? ` (${savedCard.brand})` : ""}
          </Text>
        </HStack>
      </Box>
    );
  }

  return (
    <Box mt={2}>
      {label}
      <Box
        p={3}
        mt={1}
        border="1px solid"
        borderColor="gray.200"
        borderRadius="md"
        bg="white"
      >
        {loading ? (
          <HStack spacing={2} color="gray.500">
            <Spinner size="sm" />
            <Text fontSize="sm">Loading secure payment form…</Text>
          </HStack>
        ) : error ? (
          <Text fontSize="sm" color="red.500">
            {error}
          </Text>
        ) : locationOptions ? (
          <Box>
            <Text fontSize="sm" color="gray.700" mb={2}>
              Select which location to attach your card to:
            </Text>
            <Select
              size="sm"
              placeholder="Choose a location…"
              value={chosenLid}
              onChange={(e) => setChosenLid(e.target.value)}
            >
              {locationOptions.map((loc) => (
                <option key={loc.lid} value={loc.lid}>
                  {loc.name}
                </option>
              ))}
            </Select>
            <Button
              mt={3}
              size="sm"
              bg="gray.900"
              color="white"
              _hover={{bg: "gray.800"}}
              isDisabled={!chosenLid}
              onClick={() => requestSetupIntent(chosenLid)}
            >
              Continue
            </Button>
          </Box>
        ) : setup ? (
          <EmbeddedCardSetup
            clientSecret={setup.clientSecret}
            stripePublicKey={setup.stripePublicKey}
            stripeAccount={setup.stripeAccount || null}
            onConfirm={handleConfirm}
          />
        ) : null}
      </Box>
    </Box>
  );
}
